import { useEffect, useRef, useState } from 'react';

const plans = [
  {
    id: 'trial',
    tag: '首次體驗',
    name: '體驗課',
    price: '半價',
    priceNote: '一次・約 45–60 分鐘',
    highlight: false,
    color: '#9a8470',
    bg: '#faf8f5',
    border: '#e8ddd0',
    desc: '第一次接觸古箏，感受教室環境與老師教學方式，不需要任何準備。',
    features: [
      '親手彈奏古箏，感受琴弦觸感',
      '了解自己的起點與適合的學習節奏',
      '聊聊你的生活狀態與學習期待',
      '無任何強迫報名壓力',
    ],
    cta: '預約體驗',
    ctaHref: '/contact#trial',
  },
  {
    id: 'regular',
    tag: '最多人選擇',
    name: '常規個人課',
    price: '請洽詢',
    priceNote: '每堂 60 分鐘・一對一',
    highlight: true,
    color: '#5a3e22',
    bg: '#f5f0e8',
    border: '#c8b49a',
    desc: '依個人狀況量身安排進度，沒有固定課本，從你真正的起點開始。',
    features: [
      '一對一個人化教學，不趕進度',
      '彈性安排上課時間（預約制）',
      '可選實體（台中豐原）',
      '每堂課依學員當週狀態調整內容',
      '隨時可暫停、調整學習密度',
    ],
    cta: '了解更多 / 詢問報名',
    ctaHref: 'https://line.me',
    ctaExternal: true,
  },
  {
    id: 'package',
    tag: '規律學習',
    name: '堂數方案',
    price: '請洽詢',
    priceNote: '月繳 / 季繳彈性選擇',
    highlight: false,
    color: '#4a6741',
    bg: '#eaede5',
    border: '#b8c9b0',
    desc: '適合確定要規律學習的學員，以方案計費較單堂更划算，也更有學習動力。',
    features: [
      '預購堂數，按需排課',
      '月繳或季繳方案可選',
      '方案內堂數可彈性移課',
      '長期學員有優先排課權益',
    ],
    cta: '詢問方案細節',
    ctaHref: 'https://line.me',
    ctaExternal: true,
  },
];

function useVisible(threshold = 0.1) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

export default function PricingSection() {
  const { ref, visible } = useVisible();

  return (
    <section id="pricing" className="py-20 md:py-28 bg-[#faf8f5]">
      <div className="w-full px-6 md:px-12 lg:px-20">
        {/* Header */}
        <div
          ref={ref}
          className={`mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-6 h-px bg-[#b08060]"></div>
            <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-medium uppercase">
              Pricing
            </span>
          </div>
          <h2
            className="font-serif-tc text-[#2c2420] leading-[1.3] mb-4"
            style={{ fontSize: 'clamp(1.625rem, 3vw, 2.25rem)' }}
          >
            費用說明
          </h2>
          <p className="text-[1.0625rem] leading-[1.75] text-[#5a4e42] max-w-2xl">
            課程費用依個人狀況與上課方式而有所不同。歡迎先預約體驗課，或加入 LINE@ 詢問適合你的方案。
          </p>
        </div>

        {/* Cards */}
        <div
          className={`grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 transition-all duration-700 delay-150 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl border p-7 flex flex-col transition-all duration-300 ${
                plan.highlight ? 'ring-1 ring-[#c8b49a]' : ''
              }`}
              style={{ background: plan.bg, borderColor: plan.border }}
            >
              {/* Tag */}
              <div className="flex items-center justify-between mb-5">
                <span
                  className="px-3 py-1 rounded-full text-[0.75rem] font-medium"
                  style={{ background: `${plan.color}18`, color: plan.color }}
                >
                  {plan.tag}
                </span>
                {plan.highlight && (
                  <i className="ri-star-fill text-[#c8a96e] text-sm"></i>
                )}
              </div>

              {/* Name & Price */}
              <h3 className="font-serif-tc text-[#2c2420] text-xl font-semibold mb-1">{plan.name}</h3>
              <div className="flex items-baseline gap-2 mb-1">
                <span
                  className="text-2xl font-bold"
                  style={{ color: plan.color }}
                >
                  {plan.price}
                </span>
              </div>
              <p className="text-[0.8125rem] text-[#9a8470] mb-4">{plan.priceNote}</p>

              <p className="text-[0.9375rem] leading-[1.7] text-[#5a4e42] mb-6">{plan.desc}</p>

              {/* Features */}
              <ul className="space-y-2.5 mb-8 flex-1">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-check-line text-sm" style={{ color: plan.color }}></i>
                    </div>
                    <span className="text-[0.875rem] leading-[1.6] text-[#4a3e32]">{f}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              {plan.ctaExternal ? (
                <a
                  href={plan.ctaHref}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[0.9375rem] font-medium transition-all duration-300 cursor-pointer whitespace-nowrap"
                  style={{ background: plan.color, color: 'white' }}
                >
                  <i className="ri-line-fill"></i>
                  {plan.cta}
                </a>
              ) : (
                <a
                  href={plan.ctaHref}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border text-[0.9375rem] font-medium transition-all duration-300 cursor-pointer whitespace-nowrap hover:opacity-80"
                  style={{ borderColor: plan.color, color: plan.color }}
                >
                  <i className="ri-calendar-check-line"></i>
                  {plan.cta}
                </a>
              )}
            </div>
          ))}
        </div>

        {/* Note */}
        <div
          className={`flex items-start gap-4 bg-[#f5f0e8] rounded-2xl border border-[#ede5d8] p-6 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="w-8 h-8 flex items-center justify-center shrink-0">
            <i className="ri-information-line text-[#b08060] text-lg"></i>
          </div>
          <div>
            <p className="text-[0.875rem] font-semibold text-[#3a2e22] mb-1">費用說明補充</p>
            <p className="text-[0.875rem] leading-[1.7] text-[#5a4e42]">
              實際費用依上課方式和選擇的方案不同而有所調整。
              建議先預約體驗課，或直接加入 LINE@ 告訴我你的狀況，我會提供最適合的方案建議。
              不會有任何強迫成交的情況，請安心諮詢。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
